/**
 * #132 (0101): unir mesa A na mesa B — pedidos em andamento e conta ativa
 * de A migram para B; A fica inativa apontando para B. Ação auditada
 * (audit_logs 'tables.merge').
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { tables as tablesApi } from '../lib/api.js'
import type { Table } from '../lib/api.js'
import { Button, Modal, toast } from './ui/index.js'
import { useI18n } from '../lib/i18n.js'

export function TableMergeModal({
  source,
  tables,
  onClose,
}: {
  source: Table
  tables: Table[]
  onClose: () => void
}) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const others = tables
    .filter((tb) => tb.id !== source.id && tb.is_active && !tb.merged_with_table_id)
    .sort((a, b) => a.number - b.number)
  const [targetId, setTargetId] = useState(others[0]?.id ?? '')

  const mut = useMutation({
    mutationFn: (target: string) => tablesApi.merge(source.id, target),
    onSuccess: (res) => {
      toast.success(
        t('admin.tables.merge_done', {
          from: String(res.from_table_number),
          to: String(res.to_table_number),
        }),
      )
      void qc.invalidateQueries({ queryKey: ['tables'] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['bills'] })
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open
      title={t('admin.tables.merge')}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            data-testid="tables-merge-confirm"
            disabled={!targetId || mut.isPending}
            loading={mut.isPending}
            onClick={() => targetId && mut.mutate(targetId)}
          >
            {t('admin.tables.merge')}
          </Button>
        </div>
      }
    >
      {others.length === 0 ? (
        <p className="text-sm text-gray-500">{t('admin.tables.merge_no_target')}</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-900">
            {t('admin.tables.merge_title', {
              from: String(source.number),
              to: String(others.find((o) => o.id === targetId)?.number ?? '?'),
            })}
          </p>
          <label className="block text-sm font-medium text-gray-700" htmlFor="merge-target">
            {t('admin.tables.merge_pick')}
          </label>
          <select
            id="merge-target"
            data-testid="tables-merge-target"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            {others.map((tb) => (
              <option key={tb.id} value={tb.id}>
                #{tb.number}
                {tb.label ? ` · ${tb.label}` : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500">
            {t('admin.tables.merge_hint', {
              from: String(source.number),
              to: String(others.find((o) => o.id === targetId)?.number ?? '?'),
            })}
          </p>
        </div>
      )}
    </Modal>
  )
}
